import React, { useMemo } from 'react';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { useProjectElementListStore } from '../../shared/store/projectElementStore';
import { SelectType, DisplayAlignment, DisplaySize, ImageStyle, DetailType, PEInfoType, normalizePEInfoType } from '../../shared/enum/EnumRepository';
import { WorkDetailData } from '../../shared/dto/EntityRepository';
import SelectBox from '../../shared/component/SelectBox';
import { useImageStyleJudge } from '../../shared/hooks/useImageStyleJudge';
import { ImgContentCoreWrapper, ImgWrapper, PEHeader, PEImgContentWrapper, PEIndicator, SelectBoxContainer, SelectBoxWrapper } from './Work';
import DetailInfoLeft from './DetailInfoLeft';
import DetailImgInPe from './DetailImgInPe';
import DetailVideoInPe from './DetailVideoInPe';
import DetailInfoRight from './DetailInfoRight';
import DetailInfo from './DetailInfo';

export interface DetailProps {
  peId: string;
  alignment: DisplayAlignment;
  displaySize: DisplaySize;
  infoType?: PEInfoType;
  data: WorkDetailData;
  priority: boolean;
}

const WorkDetail: React.FC<DetailProps> = ({ peId, alignment, displaySize, infoType, data, priority }) => {
  const { isEditMode } = useEditMode();
  const handleDisplayAlignmentChange = useProjectElementListStore((s) => s.updateDisplayAlignment);
  const handleDisplaySizeChange = useProjectElementListStore((s) => s.updateDisplaySize);
  const handleInfoTypeChange = useProjectElementListStore((s) => s.updateInfoType);
  const { judgeImageStyle } = useImageStyleJudge();
  const imageStyle = useMemo(() => judgeImageStyle(data.uploadFile?.width / data.uploadFile?.height), [data.uploadFile?.width, data.uploadFile?.height]);
  const effectiveInfoType = normalizePEInfoType(infoType);

  return (
    <PEImgContentWrapper>
      {isEditMode &&
        <PEHeader>
          <SelectBoxContainer>
            <SelectBoxWrapper>
              <SelectBox
                value={displaySize || DisplaySize.BIG}
                selectType={SelectType.WORK_SIZE}
                handleChange={(value) => handleDisplaySizeChange(peId, value)}
                direction={false} />
            </SelectBoxWrapper>
            <SelectBoxWrapper>
              <SelectBox
                value={alignment || DisplayAlignment.CENTER}
                selectType={SelectType.DISPLAY_ALIGNMENT}
                handleChange={value => handleDisplayAlignmentChange(peId, value)}
                direction={false} />
            </SelectBoxWrapper>
            <SelectBoxWrapper>
              <SelectBox
                value={effectiveInfoType}
                selectType={SelectType.PE_INFO_TYPE}
                handleChange={(value) => handleInfoTypeChange(peId, value)}
                direction={false} />
            </SelectBoxWrapper>
          </SelectBoxContainer>
          <PEIndicator peId={peId}>: Detail Cut</PEIndicator>
        </PEHeader>
      }
      <ImgContentCoreWrapper>
        <ImgWrapper>
          <DetailInfoLeft
            peId={peId}
            alignment={alignment || DisplayAlignment.CENTER}
            displaySize={displaySize || DisplaySize.BIG}
            imageStyle={imageStyle || ImageStyle.LANDSCAPE}
            infoType={effectiveInfoType}
            data={data} />
          {data.detailType === DetailType.VIDEO ? (
            <DetailVideoInPe
              peId={peId}
              alignment={alignment || DisplayAlignment.CENTER}
              displaySize={displaySize || DisplaySize.BIG}
              data={data}
              priority={priority}
              imageStyle={imageStyle || ImageStyle.LANDSCAPE}
            />
          ) : (
            <DetailImgInPe
              peId={peId}
              alignment={alignment || DisplayAlignment.CENTER}
              displaySize={displaySize || DisplaySize.BIG}
              data={data}
              priority={priority}
              imageStyle={imageStyle || ImageStyle.LANDSCAPE}
            />
          )}
            
          <DetailInfoRight
            peId={peId}
            alignment={alignment || DisplayAlignment.CENTER}
            displaySize={displaySize || DisplaySize.BIG}
            imageStyle={imageStyle || ImageStyle.LANDSCAPE}
            infoType={effectiveInfoType}
            data={data} />
        </ImgWrapper>
        <DetailInfo
          peId={peId}
          alignment={alignment || DisplayAlignment.CENTER}
          displaySize={displaySize || DisplaySize.BIG}
          imageStyle={imageStyle || ImageStyle.LANDSCAPE}
          infoType={effectiveInfoType}
          data={data} />
      </ImgContentCoreWrapper>
    </PEImgContentWrapper>
  );
};

export default WorkDetail;
